import { useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, ArrowRight } from "lucide-react";
import SiteBrandRow from "@/components/SiteBrandRow";
import BackendServiceNotice from "@/components/BackendServiceNotice";
import AuthFormFieldMessage from "@/components/auth/AuthFormFieldMessage";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import { getLoginFieldErrors, hasFieldErrors } from "@/lib/authFormValidation";
import { authInputClassNameLg, useAuthFormValidation } from "@/hooks/useAuthFormValidation";

const LOGIN_FIELDS = ["email", "password"];

export default function AdminLogin() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { touch, markSubmitted, shouldShow } = useAuthFormValidation(LOGIN_FIELDS);

  const errors = useMemo(
    () => getLoginFieldErrors({ email, password }),
    [email, password],
  );

  const submit = async (e) => {
    e.preventDefault();
    markSubmitted();
    if (hasFieldErrors(errors)) return;

    setLoading(true);
    try {
      await login({ kind: "admin", email: email.trim().toLowerCase(), password });
      toast.success("로그인 성공");
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err, "로그인에 실패했습니다."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="admin-login-page" className="min-h-screen bg-[#050505] text-white relative flex items-center justify-center px-6">
      <div className="absolute inset-0 bg-grid radial-mask opacity-50 pointer-events-none" />
      <div className="relative w-full max-w-md">
        <SiteBrandRow
          linkTo="/"
          size="lg"
          centered
          iconOnly
          className="mb-10"
          linkTestId="admin-login-logo-link"
          logoTestId="admin-login-logo-image"
        />

        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-8">
          <BackendServiceNotice className="mb-5" compact />
          <div className="flex items-center gap-2 mb-6">
            <Lock size={14} className="text-[#0055FF]" />
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-white/50">
              관리자 로그인
            </span>
          </div>

          <h1 className="font-display text-2xl font-medium text-white mb-2">
            대시보드 접속
          </h1>
          <p className="text-sm text-white/50 mb-8">
            관리자 계정으로 로그인하세요.
          </p>

          <form onSubmit={submit} className="space-y-4" noValidate>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/40 mb-2 block">
                이메일
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  touch("email");
                  setEmail(e.target.value);
                }}
                onBlur={() => touch("email")}
                autoComplete="email"
                data-testid="admin-email"
                aria-invalid={shouldShow("email") && errors.email ? "true" : undefined}
                className={authInputClassNameLg(shouldShow("email") && errors.email)}
                placeholder="이메일을 입력하세요"
              />
              <AuthFormFieldMessage message={shouldShow("email") ? errors.email : ""} />
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/40 mb-2 block">
                비밀번호
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  touch("password");
                  setPassword(e.target.value);
                }}
                onBlur={() => touch("password")}
                autoComplete="current-password"
                data-testid="admin-password"
                aria-invalid={shouldShow("password") && errors.password ? "true" : undefined}
                className={authInputClassNameLg(shouldShow("password") && errors.password)}
                placeholder="비밀번호를 입력하세요"
              />
              <AuthFormFieldMessage message={shouldShow("password") ? errors.password : ""} />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="btn-primary w-full justify-center disabled:opacity-50"
            >
              {loading ? "로그인 중..." : "로그인"}
              <ArrowRight size={14} />
            </button>
          </form>
          <p className="text-xs text-white/40 mt-5 text-center">
            일반 회원이신가요?{" "}
            <Link to="/login" className="text-white underline hover:text-white/90">
              기본 로그인
            </Link>
          </p>
        </div>
      </div>
    </main>
  );
}
